import { EXPLORE_IMAGES } from './exploreImages';

export const TREKKING_HUB = {
	title: 'Trekking in Nepal',
	lead: 'Nepal is the world\'s greatest teahouse trekking country — from gentle valley walks to 5,500m passes. This guide maps the major routes so you pick the right trek for your fitness, season, and time.',
	hero: EXPLORE_IMAGES.trekking,
	quickFacts: [
		{ value: 'EBC · ABC · Annapurna', label: 'Classic routes' },
		{ value: 'Mar–May · Oct–Nov', label: 'Best seasons' },
		{ value: 'Teahouse', label: 'Lodge-to-lodge style' },
		{ value: 'TIMS / Park permits', label: 'Required on most routes' },
	],
	sections: [
		{
			id: 'why',
			question: 'Why trek in Nepal?',
			answer: 'Nowhere else combines multi-day Himalayan trekking with village lodges, hot meals, and no camping gear on classic routes. You walk through Sherpa, Tamang, and Gurung villages, cross suspension bridges, and wake to eight-thousanders — all on footpaths used for centuries.',
			tips: ['Teahouse trekking means you sleep and eat in lodges — not wild camping.', 'Altitude — not distance — is the main challenge on high routes.'],
		},
		{
			id: 'classic',
			question: 'The classic treks — which is right for you?',
			answer: 'Everest Base Camp (12–14 days, strenuous, 5,364m): the iconic Khumbu route via Lukla. Annapurna Base Camp (7–10 days, moderate–hard, 4,130m): shorter, forest-to-glacier drama. Annapurna Circuit (14–18 days, strenuous): Thorong La pass at 5,416m — full cultural loop. Langtang Valley (7–9 days, moderate): closest to Kathmandu, Tamang heritage. Ghorepani–Poon Hill (4–5 days, easy): best first trek with sunrise on Annapurna. Upper Mustang (10–12 days, moderate): desert canyon, restricted permit. Manaslu Circuit (14–16 days, strenuous): quieter alternative to Annapurna.',
			tips: ['First high-altitude trek? Consider Poon Hill or Langtang before EBC.', 'October and April are busiest — book Lukla flights and lodges early.'],
		},
		{
			id: 'permits',
			question: 'Permits and regulations',
			answer: 'Most treks need a national park or conservation area permit plus TIMS (Trekkers\' Information Management System) on some routes. Khumbu (Everest) uses a local Khumbu permit instead of TIMS. Restricted areas (Upper Mustang, Manaslu, Dolpo) require a licensed guide and special permits. Check current fees on NTB and DNPWC websites before travel.',
			tips: ['Never trek without insurance that covers your maximum altitude.', 'Some routes legally require a guide — verify before booking independently.'],
		},
		{
			id: 'fitness',
			question: 'Fitness and altitude',
			answer: 'EBC and high passes demand good cardio fitness and patience for acclimatization. Build stair-climbing and hill walking into your prep 2–3 months ahead. Never skip acclimatization days — altitude sickness is the main reason trekkers turn back, not leg fitness.',
			tips: ['Walk 6–8 hours on consecutive days at home before you fly.', 'Learn AMS symptoms: headache, nausea, dizziness — descend if worsening.'],
		},
		{
			id: 'guided',
			question: 'Independent vs guided',
			answer: 'Classic EBC and Annapurna routes are commonly trekked independently — trails are well-marked and lodges abundant. Guides add navigation help, altitude judgment, and cultural context. Porter optional but eases load. Peak season (October) — a guide helps with lodge booking.',
			tips: ['Our Everest Base Camp guided journey walks you through permits, packing, and day-by-day planning step by step.'],
		},
	],
	faqs: [
		{ q: 'What is the easiest trek for beginners?', a: 'Ghorepani–Poon Hill (4–5 days from Pokhara) or Kathmandu Valley rim walks. Gentle elevation, great views, good lodges.' },
		{ q: 'Do I need a guide for Everest Base Camp?', a: 'Not legally required on the standard Lukla–EBC route. Many first-timers hire a guide or join a group for peace of mind.' },
		{ q: 'How much does a trek cost?', a: 'Budget teahouse trek: $25–40/day on trail for food and lodge. Add permits, domestic flights, gear, and tips.' },
	],
};

export const TEMPLES_HUB = {
	title: 'Temples & sacred sites',
	lead: 'Nepal is a living museum of Hindu and Buddhist sacred architecture — from Kathmandu Valley UNESCO squares to hilltop stupas and remote monasteries. Visit respectfully: these are active places of worship, not photo backdrops.',
	hero: EXPLORE_IMAGES.monastery,
	quickFacts: [
		{ value: '10', label: 'UNESCO World Heritage sites' },
		{ value: 'Hindu + Buddhist', label: 'Living traditions' },
		{ value: 'Valley + hills', label: 'Where to start' },
		{ value: 'Dress modestly', label: 'Temple etiquette' },
	],
	sections: [
		{
			id: 'valley',
			question: 'Kathmandu Valley — the essential seven',
			answer: 'Pashupatinath: Nepal\'s holiest Hindu temple on the Bagmati River — cremation ghats, sadhus, evening aarti. Boudhanath: one of the largest stupas in the world — Tibetan Buddhist heart of Kathmandu, kora walk at dusk. Swayambhunath (Monkey Temple): hilltop stupa overlooking the valley — mix of Buddhist and Hindu iconography. Kathmandu Durbar Square: Hanuman Dhoka palace complex, Kumari (living goddess) house. Patan Durbar Square: finest Newari metalwork and stone carving. Bhaktapur Durbar Square: medieval city feel, pottery square, Nyatapola Temple. Changu Narayan: oldest temple in the valley, hilltop north of Bhaktapur.',
			tips: ['Start at Boudhanath at sunset — butter lamps and prayer wheels.', 'Pashupatinath: non-Hindus cannot enter main temple but can view from east bank.'],
		},
		{
			id: 'beyond',
			question: 'Beyond the valley',
			answer: 'Lumbini: birthplace of Buddha — Maya Devi Temple, monastic zone from many countries. Muktinath: sacred to Hindus and Buddhists at 3,800m in Mustang — eternal flame and 108 water spouts. Janaki Mandir (Janakpur): ornate Hindu temple in the Terai. Gosainkunda: alpine lake pilgrimage site in Langtang region. Tengboche Monastery: iconic Everest region monastery with Ama Dablam backdrop.',
			tips: ['Lumbini pairs well with a Chitwan wildlife extension.', 'Muktinath requires flight or long jeep via Jomsom.'],
		},
		{
			id: 'etiquette',
			question: 'How to visit respectfully',
			answer: 'Remove shoes before entering temple interiors. Cover shoulders and knees — carry a scarf. Walk clockwise around Buddhist stupas and chortens. Do not touch offerings, statues, or prayer flags. Ask before photographing people — especially sadhus and monks. Some inner sanctums prohibit photography entirely.',
			tips: ['Morning (6–9am) is when locals worship — most atmospheric and respectful time.', 'Donations are optional — give at official boxes, not aggressive touts.'],
		},
		{
			id: 'festivals',
			question: 'Temples during festivals',
			answer: 'Dashain and Tihar bring extraordinary activity at Pashupatinath and valley shrines. Buddha Jayanti fills Boudhanath and Lumbini. Indra Jatra and Machhindranath chariot processions transform Durbar Squares. Shivaratri draws hundreds of thousands to Pashupatinath. Plan accommodation early if your dates overlap major festivals.',
			tips: ['See our Festivals guide for lunar calendar dates — they shift every year.'],
		},
		{
			id: 'newari',
			question: 'Newari temple architecture',
			answer: 'The Kathmandu Valley\'s indigenous Newar community built tiered pagoda temples in brick and carved wood — unlike anything in India or Tibet. Patan and Bhaktapur are the best places to study this style. Look for struts carved with deities, gilded roofs, and stone lions guarding steps.',
			tips: ['Hire a local guide in Patan for one hour — the symbolism rewards explanation.'],
		},
	],
	faqs: [
		{ q: 'Can I enter temples wearing shorts?', a: 'No — modest clothing required. Carry a light shawl or pants to cover up.' },
		{ q: 'Which temple is best at sunrise?', a: 'Swayambhunath for valley views; Boudhanath for atmosphere with early kora walkers.' },
		{ q: 'Are there entry fees?', a: 'UNESCO Durbar Squares charge foreign visitor fees (NPR 1,000). Boudhanath and Swayambhunath have nominal fees.' },
	],
};
